/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// react and redux
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams, useLocation } from "react-router-dom";
// redux
import { useSelector, useDispatch } from "react-redux";
import { selectUser } from "../../../store/userSlice";
import authRoles from "../../../auth/authRoles";
// actions
import { getListing, addInstance, updateInstance, deleteInstance } from "../../../actions";
// forms
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
// BASE Components
import _ from "../../../../@lodash";
import BASELoading from "../../../../@BASE/core/BASELoading";
// material-ui components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteIcon from '@mui/icons-material/Delete';
// custom components
import FormSectionHeader from "../../../shared-components/crud/instance/FormSectionHeader";
import CourseStatusSteps from "./CourseStatusSteps";
import DeleteModal from "../../../shared-components/crud/DeleteModal";

// form validation
const schema = yup.object().shape({
  project: yup.string().required("You must enter a project id"),
  label: yup.string().required("You must enter a label"),
  language: yup.object().shape({
    title: yup.string().required("You must select a language"),
    acronym: yup.string().required("You must select a language"),
    rtl: yup.boolean().required("You must select a language"),
  }),
  title: yup.string().required("You must enter a title"),
  classification: yup.string().required("You must enter a classification"),
  enrichment_link: yup.string().required("You must enter an enrichment link"),
  order: yup.number().typeError("Order must be a number").required("You must enter an order"),
});

const CourseForm = (props) => {
  const { instance, handleInstanceAction, loading, setOnSave, setSaveDisabled } = useOutletContext();
  const [projects, setProjects] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = React.useState({
    title: "English",
    acronym: "en",
    rtl: false,
  });
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeParams = useParams();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const project = instance?.project?.id ?? queryParams.get('p');
  const languages = {
    en: { title: 'English', acronym: 'en', rtl: false },
    ad: { title: 'Arabic', acronym: 'ad', rtl: true },
    cm: { title: 'Chinese', acronym: 'cm', rtl: false },
    fr: { title: 'French', acronym: 'fr', rtl: false },
    hj: { title: 'Hindi', acronym: 'hj', rtl: false },
    jn: { title: 'Indonesian', acronym: 'jn', rtl: false },
    jt: { title: 'Italian', acronym: 'jt', rtl: false },
    ja: { title: 'Japanese', acronym: 'ja', rtl: false },
    kp: { title: 'Korean', acronym: 'kp', rtl: false },
    pf: { title: 'Persian Farsi', acronym: 'pf', rtl: false },
    py: { title: 'Portuguese', acronym: 'py', rtl: false },
    ru: { title: 'Russian', acronym: 'ru', rtl: false },
    qb: { title: 'Spanish', acronym: 'qb', rtl: false },
  };
  // form methods
  const { control, watch, reset, handleSubmit, formState, setValue, getValues } = useForm({
    mode: "all",
    resolver: yupResolver(schema),
  });
  // form state
  const { isValid, dirtyFields, errors } = formState;
  // watch form
  const form = watch();
  // get parent listing
  const handleGetProjects = async () => {
    try {
      const response = await dispatch(getListing('projects')()).unwrap();
      setProjects(response);
      setValue('project', project);
    } catch (err) {
      console.log(err.message);
    }
  }
  // ensure projects are only fetched once
  useEffect(() => {
    // get users
    handleGetProjects();
  }, [dispatch, routeParams]);  
  // form submission
  function onSubmit(data) {
    // update the language object before submitting.
    data.language = selectedLanguage;
    // update the form data to include the user id
    data.user = user._id;
    // dispatch add or update course
    if (routeParams.id === 'new') {
      handleInstanceAction(addInstance('courses')(data), 'post');
    } else {
      handleInstanceAction(updateInstance('courses')(data), 'patch');
    }
  }
  // Pass the handleSubmit function to setOnSave
  useEffect(() => {
    setOnSave(() => handleSubmit(onSubmit));
  }, []);
  // Update the parent component with the form's validity and dirty state
  useEffect(() => {
    setSaveDisabled(_.isEmpty(dirtyFields) || !isValid);
  }, [formState]);
  // handle removal of course
  const handleRemoveCourse = () => {
    // build request data
    const data = {
        id: instance.id,
        data: {
          user: user._id || user.id
        }
      };
    handleInstanceAction(deleteInstance('courses')(data), 'delete');
    // navigate to projects listing
    navigate('/projects');
  }
  // on instance change, reset the form
  useEffect(() => {
    let data = {
      ...instance,
      project: project
    }
    reset({ ...data });
  }, [instance, reset]);
  // if data is missing, return loading
  if (_.isEmpty(form) || !instance || !projects) {
    return <BASELoading />;
  }
  
  return (
    <div className="relative flex flex-col flex-auto">
      <div className="relative my-64 px-24 sm:px-32 max-w-4xl">
        <FormSectionHeader title="Basic Information" description="Enter the basic course details below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  placeholder="Title"
                  id="title"
                  error={!!errors.title}
                  helperText={errors?.title?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="label"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Label"
                  placeholder="Label"
                  id="label"
                  error={!!errors.label}
                  helperText={errors?.label?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="classification"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Classification"
                  placeholder="Classification"
                  id="label"
                  error={!!errors.classification}
                  helperText={errors?.classification?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
        </div>

        <Divider className="my-40" />

        <FormSectionHeader title="Course Details" description="Enter the course details below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.project}
              required
              disabled
            >
              <InputLabel id="project">Project</InputLabel>
              <Controller
                control={control}
                name="project"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Projects"
                    placeholder="Projects"
                    id="project"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Project</MenuItem>
                    {projects.map((project) => (
                      <MenuItem value={project.id} key={project.id} className="space-x-12">
                        <span>{project.title}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.language}
              required
            >
              <InputLabel id="language">Language</InputLabel>
              <Controller
                control={control}
                name="language.acronym"
                defaultValue=''
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                      setSelectedLanguage(languages[e.target.value]);
                    }}
                    label="Language"
                    placeholder="Language"
                    id="language"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a language</MenuItem>
                    {Object.keys(languages).map((key) => (
                      <MenuItem key={key} value={languages[key].acronym}>
                        {languages[key].title}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="order"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Order"
                  placeholder="Order"
                  id="order"
                  error={!!errors?.order}
                  helperText={errors?.order?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="enrichment_link"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Enrichment Link"
                  placeholder="Enrichment Link"
                  id="enrichment_link"
                  error={!!errors.enrichment_link}
                  helperText={errors?.enrichment_link?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
        </div>

        <Divider className="my-40" />

        <FormSectionHeader title="Course Status" description="These values are here for progress and status." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Typography variant='body2' className='font-semibold'>Progress:</Typography>
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="progress.development"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Storyboard Progress"
                  placeholder="Storyboard Progress"
                  id="progress_development"
                  error={!!errors?.progress?.development}
                  helperText={errors?.progress?.development?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="progress.production"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="IMI Progress"
                  placeholder="IMI Progress"
                  id="progress_production"
                  error={!!errors?.progress?.production}
                  helperText={errors?.progress?.production?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-4">
            <Typography variant='body2' className='font-semibold'>Status:</Typography>
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors?.status?.development}
              required
            >
              <InputLabel id="status_development">Storyboard Status</InputLabel>
              <Controller
                control={control}
                name="status.development"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Storyboard Status"
                    placeholder="Storyboard Status"
                    id="status_development"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Status</MenuItem>
                    {Object.keys(CourseStatusSteps).map((key) => (
                      <MenuItem value={key} key={key} className="space-x-12">
                        <span>{CourseStatusSteps[key].label}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors?.status?.production}
              required
            >
              <InputLabel id="status_production">IMI Status</InputLabel>
              <Controller
                control={control}
                name="status.production"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Storyboard Status"
                    placeholder="Storyboard Status"
                    id="status_production"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Status</MenuItem>
                    {Object.keys(CourseStatusSteps).map((key) => (
                      <MenuItem value={key} key={key} className="space-x-12">
                        <span>{CourseStatusSteps[key].label}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
        </div>
      </div>

      <Box
        className="flex items-center mt-40 py-14 pr-16 pl-4 sm:pr-48 sm:pl-36 border-t"
        sx={{ backgroundColor: 'background.default' }}
      >
        {routeParams.id !== 'new' && (
          authRoles.admin.includes(user.role) && (
            <DeleteModal onDelete={handleRemoveCourse} resource="course" />
          )
        )}
        <Button
          component={Link}
          className="ml-auto"
          to="/projects"
        >
          Cancel
        </Button>
        <Button
          className="ml-8"
          variant="contained"
          color="secondary"
          disabled={_.isEmpty(dirtyFields) || !isValid}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </Box>
    </div> 
  );
};

export default CourseForm;
